import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Eye, Trash2, Upload, Pencil, FolderOpen } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { DOC_CATEGORIES, DOC_AUDIENCES, DOC_STUDENT_TRACKS, categoryLabel, audienceLabel, studentTrackLabel, formatSize, openSharedDoc } from "@/lib/sharedDocs";

const EMPTY = { title: "", description: "", category: "regolamenti", audience: "entrambi", student_track: "tutti" };

function Picker({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function DocDialog({ open, onClose, editing }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  React.useEffect(() => {
    if (open) { setForm(editing ? { ...EMPTY, ...editing, student_track: editing.student_track || "tutti" } : EMPTY); setFile(null); }
  }, [open, editing]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        const { title, description, category, audience, student_track } = form;
        await api.patch(`/shared-documents/${editing.id}`, { title, description, category, audience, student_track });
        if (file) {
          const fd = new FormData(); fd.append("file", file);
          await api.post(`/shared-documents/${editing.id}/file`, fd);
        }
        return;
      }
      const fd = new FormData();
      ["title", "description", "category", "audience", "student_track"].forEach((k) => fd.append(k, form[k]));
      fd.append("file", file);
      await api.post("/shared-documents", fd);
    },
    onSuccess: () => {
      toast.success(editing ? "Documento aggiornato" : "Documento pubblicato e notificato");
      qc.invalidateQueries({ queryKey: ["shared-docs-admin"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Errore durante il salvataggio"),
  });

  const valid = form.title.trim().length >= 2 && (editing || file);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="shared-doc-dialog">
        <DialogHeader><DialogTitle>{editing ? "Modifica documento" : "Nuovo documento condiviso"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Titolo</Label><Input value={form.title} onChange={set("title")} placeholder="Es. Regolamento interno" data-testid="shared-doc-title-input" /></div>
          <div><Label>Descrizione (facoltativa)</Label><Textarea rows={2} value={form.description} onChange={set("description")} data-testid="shared-doc-description-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Categoria</Label><Picker value={form.category} onChange={set("category")} options={DOC_CATEGORIES} testId="shared-doc-category-select" /></div>
            <div><Label>Visibile a</Label><Picker value={form.audience} onChange={set("audience")} options={DOC_AUDIENCES} testId="shared-doc-audience-select" /></div>
            {form.audience !== "docenti" && (
              <div className="sm:col-span-2"><Label>Quali studenti</Label><Picker value={form.student_track} onChange={set("student_track")} options={DOC_STUDENT_TRACKS} testId="shared-doc-track-select" /></div>
            )}
          </div>
          <div>
            <Label>{editing ? "Sostituisci file (facoltativo)" : "File"}</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="shared-doc-file-input"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.odt,.txt" />
            {editing && <div className="text-[11px] text-stone-500 mt-1">File attuale: {editing.original_filename}</div>}
          </div>
          {!editing && <div className="text-xs text-stone-500">Alla pubblicazione i destinatari ricevono un avviso in bacheca e una notifica push.</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="shared-doc-cancel-btn">Annulla</Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending} data-testid="shared-doc-save-btn">
            {save.isPending ? "Salvataggio…" : editing ? "Salva" : "Pubblica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminRow({ d, onEdit, onDelete }) {
  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-shared-doc-${d.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-stone-50 border border-stone-200 grid place-items-center shrink-0">
          <FileText size={18} className={d.extension === "pdf" ? "text-red-600" : "text-stone-500"} />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-[#1C352D] truncate">{d.title}</div>
          <div className="text-[11px] text-stone-500 truncate">{d.original_filename} · {formatSize(d.size)} · {fmtDate(d.created_at)}</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            <Badge variant="outline">{categoryLabel(d.category)}</Badge>
            <Badge variant="outline" data-testid={`admin-shared-doc-audience-${d.id}`}>{audienceLabel(d.audience)}</Badge>
            {d.audience !== "docenti" && <Badge variant="outline" data-testid={`admin-shared-doc-track-${d.id}`}>{studentTrackLabel(d.student_track)}</Badge>}
            <Badge variant="secondary" data-testid={`admin-shared-doc-views-${d.id}`}><Eye size={11} className="mr-1" />{d.views_count} letture</Badge>
          </div>
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <Button size="sm" variant="ghost" onClick={() => openSharedDoc(d.id)} data-testid={`admin-shared-doc-open-${d.id}`}><Upload size={14} className="rotate-180" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onEdit(d)} data-testid={`admin-shared-doc-edit-${d.id}`}><Pencil size={14} /></Button>
        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(d)} data-testid={`admin-shared-doc-delete-${d.id}`}><Trash2 size={14} /></Button>
      </div>
    </div>
  );
}

export default function DocumentiCondivisi() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["shared-docs-admin"],
    queryFn: async () => (await api.get("/shared-documents")).data,
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/shared-documents/${id}`),
    onSuccess: () => { toast.success("Documento eliminato"); qc.invalidateQueries({ queryKey: ["shared-docs-admin"] }); },
  });
  const onDelete = (d) => { if (window.confirm(`Eliminare «${d.title}»? Non sarà più visibile a nessuno.`)) del.mutate(d.id); };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto" data-testid="documenti-condivisi-page">
      <PageHeader
        title="Documenti condivisi"
        subtitle="Carica una sola volta regolamenti, moduli e calendari: li vedono tutti gli studenti e/o i docenti."
        actions={<Button onClick={() => setDialog({ open: true, editing: null })} data-testid="shared-doc-new-btn"><Plus size={16} className="mr-1.5" />Nuovo documento</Button>}
      />
      {isLoading ? (
        <div className="text-sm text-stone-400 text-center py-8">Caricamento…</div>
      ) : docs.length === 0 ? (
        <Card className="p-10 text-center text-sm text-stone-400 border border-stone-200 rounded-xl shadow-none">
          <FolderOpen size={28} className="mx-auto mb-3 text-stone-300" />Nessun documento condiviso. Carica il primo, ad esempio il regolamento.
        </Card>
      ) : (
        <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
          <div className="divide-y divide-stone-100">
            {docs.map((d) => <AdminRow key={d.id} d={d} onEdit={(x) => setDialog({ open: true, editing: x })} onDelete={onDelete} />)}
          </div>
        </Card>
      )}
      <DocDialog open={dialog.open} editing={dialog.editing} onClose={() => setDialog({ open: false, editing: null })} />
    </div>
  );
}
